import { useCallback, useEffect, useRef, useState } from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";

import { uploadFile } from "services";
import {
  labelUploadFile,
  errorFileNotSupported,
  errorFileAlreadyPresent,
  errorFileLimitMax,
  erroreCampoObbligatorio,
} from "_constants";

import { FileUpload, FileUploadSelectEvent } from "primereact/fileupload";
import { HeaderTemplate, ItemTemplate } from ".";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  setStatusUploadSource,
  StatusUploadSource,
} from "store/slicer/FormAddSourceValidationSlice";

export const UploadSource = ({
  setValue,
  getValues,
  onFocusName,
  control,
  projectId,
}: {
  setValue: UseFormSetValue<{
    fileId: string;
    name: string;
    author: string;
    isbn: string;
    topicId: string;
  }>;
  getValues: UseFormGetValues<{
    fileId: string;
    name: string;
    author: string;
    isbn: string;
    topicId: string;
  }>;
  control: Control<
    {
      fileId: string;
      name: string;
      author: string;
      isbn: string;
      topicId: string;
    },
    any
  >;
  onFocusName: Function;
  projectId?: string;
}) => {
  const [errorFile, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean | null>(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const abortController = useRef<AbortController | null>(null);
  const loadingRef = useRef(loading);
  const MAX_FILE_SIZE = 150 * 1024 * 1024;
  const [visibleHeader, setVisibleHeader] = useState(true);
  const fileUploadRef = useRef<FileUpload>(null);
  const dispatch = useAppDispatch();
  const statusUploadSource = useAppSelector(
    (state) => state.formAddSourceValidationSlice.statusUploadSource
  );
  const source = useAppSelector((state) => state.gestioneSourceSlice);

  const interruptUpload = useCallback(() => {
    const currentFile = fileUploadRef.current?.getFiles();
    if (currentFile?.length) {
      if (abortController.current) {
        abortController.current.abort();
        fileUploadRef.current?.clear();
        setValue("fileId", "");
        setVisibleHeader(true);
        setUploadPercentage(0);
        dispatch(setStatusUploadSource(StatusUploadSource.WAITING));
      }
    }
  }, [dispatch, setValue]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [getValues, loading, uploadPercentage]);

  useEffect(() => {
    if (statusUploadSource === StatusUploadSource.CANCEL) {
      interruptUpload();
    }
  }, [statusUploadSource, interruptUpload, dispatch]);

  const processFiles = useCallback(
    (files: File[]) => {
      if (files && files.length > 0) {
        setVisibleHeader(false);
        if (files[0].size > MAX_FILE_SIZE) {
          setError(errorFileLimitMax);
          setVisibleHeader(true);
          return;
        }
        const extension = files[0].name.split(".").pop();
        if (
          extension === "pdf" ||
          extension === "docx" ||
          extension === "txt"
        ) {
          setError(null);
          setUploadPercentage(0);
          setLoading(true);
          const formData = new FormData();
          formData.append("file", files[0]);

          if (abortController.current) {
            abortController.current.abort();
          }

          abortController.current = new AbortController();
          dispatch(setStatusUploadSource(StatusUploadSource.UPLOADING));
          if (projectId) {
            formData.append("projectId", projectId);
          }
          uploadFile(
            formData,
            abortController.current.signal,
            setUploadPercentage
          )
            .then((res) => {
              setValue("fileId", res.data.fileId);
              const lastDotIndex = files[0].name.lastIndexOf(".");
              const name =
                lastDotIndex !== -1
                  ? files[0].name.substring(0, lastDotIndex)
                  : files[0].name;
              if (!getValues("name")) {
                setValue("name", name);
                onFocusName();
              }
            })
            .catch((error) => {
              fileUploadRef.current?.clear();
              setValue("fileId", "");
              setVisibleHeader(true);
              setUploadPercentage(0);
              if (error.response && error.response.status === 415) {
                setError(errorFileNotSupported);
              }
              if (error.response && error.response.status === 409) {
                setError(errorFileAlreadyPresent);
              }
              if (error.response && error.response.status === 413) {
                setError(errorFileLimitMax);
              }
            })
            .finally(() => {
              dispatch(setStatusUploadSource(StatusUploadSource.WAITING));
              setLoading(false);
            });
        } else {
          setError(errorFileNotSupported);
          setVisibleHeader(true);
        }
      }
    },
    [MAX_FILE_SIZE, setValue, getValues, onFocusName, dispatch, projectId]
  );

  const handleCancelUpload = (_: File) => {
    interruptUpload();
  };

  const deleteFile = () => {
    setValue("fileId", "");
    setValue("name", "");

    fileUploadRef.current?.clear();
    setVisibleHeader(true);
  };

  useEffect(() => {
    if (getValues("fileId")) {
      setVisibleHeader(false);
    }
  }, [getValues]);

  const chooseOptions = {
    label: labelUploadFile,
    iconOnly: false,
    className: "padd-0 custom-choose-btn p-button p-component p-button-link",
  };

  const onTemplateSelect = async (event: FileUploadSelectEvent) => {
    const files = event.files;

    processFiles(files);
  };

  const headerTemplate = (options: any) => {
    return (
      <HeaderTemplate
        options={options}
        visibleHeader={visibleHeader}
        error={errorFile}
      />
    );
  };

  const itemTemplate = (file: any, props: any) => {
    return (
      <ItemTemplate
        file={file}
        props={props}
        loading={loading}
        uploadPercentage={uploadPercentage}
        deleteFile={deleteFile}
        handleCancelUpload={handleCancelUpload}
        fileUploadRef={fileUploadRef}
      />
    );
  };

  const onBeforeSelect = (e: any) => {
    e.originalEvent.stopPropagation();
  };

  const emptyTemplate = () => {
    if (getValues("fileId")) {
      const File = {
        name: source.fileName,
        size: source.size,
        fileType: source.fileType,
      };
      return itemTemplate(File, {});
    } else {
      return <div className="file-upload-empty-template"></div>;
    }
  };

  return (
    <>
      <Controller
        name="fileId"
        control={control}
        rules={{
          validate: {
            required: (value) => {
              if (value?.length) return true;
              else {
                setError(erroreCampoObbligatorio);
                return false;
              }
            },
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <div>
            <FileUpload
              id={field.name}
              {...field}
              ref={fileUploadRef}
              progressBarTemplate={<></>}
              accept=".pdf,.txt,.docx"
              maxFileSize={MAX_FILE_SIZE}
              onBeforeSelect={onBeforeSelect}
              onSelect={onTemplateSelect}
              headerTemplate={headerTemplate}
              emptyTemplate={emptyTemplate}
              itemTemplate={itemTemplate}
              chooseOptions={chooseOptions}
              className="custom-file-upload"
              customUpload
            />
            {errorFile && (
              <span className="text-error margin-top-4">{errorFile}</span>
            )}
          </div>
        )}
      />
    </>
  );
};
